import { Card, CardHeader, Combobox, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';
import { useState } from 'react';
import { ErrorCard, LoadingCard } from '../../app/data-view.js';
import { getApiError } from '../../lib/api.js';
import { revenueAccountsApi, type AccountingAccount, type RevenueNature } from './api.js';

const KEY = ['company', 'accounting', 'revenue-accounts'];
const NATURES: Array<{ nature: RevenueNature; label: string }> = [
  { nature: 'SERVICE', label: 'Serviços' },
  { nature: 'PRODUCT', label: 'Produtos' },
  { nature: 'RETURN', label: 'Devoluções' },
  { nature: 'RETENTION', label: 'Retenções' },
];
export function RevenueAccounts({
  accounts,
  canManage,
}: {
  accounts: AccountingAccount[];
  canManage: boolean;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const [busy, setBusy] = useState(false);
  const query = useQuery({ queryKey: KEY, queryFn: revenueAccountsApi.list });
  const options = accounts
    .filter((a) => a.active && !accounts.some((child) => child.code.startsWith(`${a.code}.`)))
    .map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }));
  async function save(nature: RevenueNature, id: string) {
    setBusy(true);
    try {
      const next = await revenueAccountsApi.set(nature, id || null);
      client.setQueryData(KEY, next);
      await client.invalidateQueries({ queryKey: ['company', 'accounting', 'accounts'] });
      snackbar.success('Destino da receita das vendas salvo.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader title="Receita das vendas" icon={<Receipt className="size-4" />} />
      <p className="mb-4 text-sm text-content-muted">
        Escolha a conta do plano de cada natureza, para todas as unidades. Pacotes se dividem pelos
        itens. Devoluções reduz o resultado no mês do cancelamento; a sugestão é 2.4 Devoluções e
        cancelamentos de vendas. Retenções é a parte do adiantamento que fica com a empresa quando o
        cliente cancela em cima da hora; a sugestão é 1.4.1 Retenções por cancelamento.
      </p>
      {query.isPending ? (
        <LoadingCard label="Carregando o destino da receita das vendas" />
      ) : query.isError ? (
        <ErrorCard message={getApiError(query.error)} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {NATURES.map(({ nature, label }) => {
            const chosen = query.data.find((one) => one.nature === nature)?.account;
            return canManage ? (
              <Combobox
                key={nature}
                label={label}
                placeholder="Buscar conta do plano"
                selectedLabel={chosen ? `${chosen.code} ${chosen.name}` : 'Sem conta do plano'}
                options={[{ value: '', label: 'Sem conta do plano' }, ...options]}
                disabled={busy}
                onSelect={(choice) => {
                  if (choice.kind === 'option') void save(nature, choice.value);
                }}
              />
            ) : (
              <div key={nature}>
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-sm text-content-muted">
                  {chosen ? `${chosen.code} ${chosen.name}` : 'Sem conta do plano'}
                </p>
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-4 text-xs text-content-muted">
        Cancelar com crédito reverte a receita; usar o crédito reconhece a receita da nova venda,
        sem entrada de dinheiro no caixa.
      </p>
    </Card>
  );
}
